import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Contract, Milestone } from '../types';
import { canAttestMilestone, milestoneAttestationActionLabel } from '../utils/milestoneAttestation';
import { MilestoneConflictActionButton } from './MilestoneConflictActionButton';
import { MilestoneRemediationActions } from './MilestoneRemediationActions';

export type MilestoneActionSide = 'company' | 'person';
export type MilestoneActionMode = 'attestation' | 'resolution';

type ActionContract = Pick<Contract, 'draftStatus' | 'companySigned' | 'personSigned'>;

/**
 * What the viewer does with a stage. The project view opens its attestation
 * modal; the start page opens the same modal in the project view.
 */
export interface MilestoneActionHandlers {
  onCheckPayout?: (milestone: Milestone) => void;
  checkingPayoutId?: string | null;
  onOpen: (milestone: Milestone, mode?: MilestoneActionMode) => void;
  /** Company side: resubmits a remedied stage, then opens its attestation. */
  onAttestRemedied?: (milestone: Milestone) => void;
  /** The stage whose remedy is being resubmitted (shows a spinner). */
  preparingMilestoneId?: string | null;
}

const DIALOGUE_STATUSES = ['disputed', 'conflict', 'external_dispute', 'under_resolution'];

/** The stages a contract offers the viewer to act on, shared by the project view and the start page. */
export function contractMilestoneActionState(
  contract: ActionContract,
  milestones: Milestone[],
  projectFunded: boolean,
  side: MilestoneActionSide
) {
  const attestableMilestones = contract.draftStatus === 'signed_by_both'
    ? milestones
        .filter(candidate => canAttestMilestone(candidate, side))
        .sort((left, right) => left.milestoneNumber - right.milestoneNumber)
    : [];
  const disputeDialogueMilestone = milestones.find(
    milestone => DIALOGUE_STATUSES.includes(milestone.deliveryStatus ?? '')
  );
  const milestoneActionTarget = disputeDialogueMilestone ?? attestableMilestones[0] ?? null;
  return { attestableMilestones, disputeDialogueMilestone, milestoneActionTarget };
}

/**
 * An open objection (a disputed stage not in conflict mode) where it is the
 * other party's turn: the viewer has nothing to do until they reply, so the
 * contract card shows no "Att göra" for it (ClickUp 869fj6n8x).
 */
export const objectionAwaitsOtherParty = (
  milestone: Milestone | undefined,
  side: MilestoneActionSide,
  objectionTurn?: (milestone: Milestone) => MilestoneActionSide
) => !!milestone && !!objectionTurn && milestone.deliveryStatus === 'disputed' && !milestone.conflictStartedAt &&
  objectionTurn(milestone) !== side;

const useActionLabels = () => {
  const { t } = useTranslation();
  const subjectLabel = (milestone: Milestone) => milestone.itemType === 'variation_price_adjustment'
    ? milestone.amount === 0 ? t('projectView.ata.change') : t('projectView.ata.priceChange')
    : t('projectView.dispute.objectionNumber', { number: milestone.milestoneNumber });
  const attestLabel = (milestone: Milestone) => milestone.itemType === 'variation_price_adjustment'
    ? t('projectView.ata.attest')
    : milestoneAttestationActionLabel(t('personHome.projectTimeline.attest', 'Attestera etappmål'), milestone);
  // The stage named in the "Att göra" row: "Etappmål #3", or the price
  // adjustment's own label ("ÄTA-prisändring").
  const stageSubject = (milestone: Milestone) => milestone.itemType === 'variation_price_adjustment'
    ? milestone.amount === 0 ? t('projectView.ata.change') : t('projectView.ata.priceChange')
    : t('projectView.overview.next.stageNumber', { number: milestone.milestoneNumber });
  /**
   * The text beside a dialogue stage's button in "Att göra", like the attest
   * rows: what the case is (bold) and what happens now (ClickUp 869fme8gr).
   * Same ladder as the status overview: objection → conflict → dispute.
   */
  const dialogueRowText = (
    milestone: Milestone,
    side: MilestoneActionSide,
    objectionTurn?: (milestone: Milestone) => MilestoneActionSide
  ) => {
    const subject = stageSubject(milestone);
    const isPerson = side === 'person';
    const inConflict = milestone.deliveryStatus === 'conflict' || !!milestone.conflictStartedAt;
    if (milestone.deliveryStatus === 'external_dispute') {
      return { title: t('projectView.contractTodo.externalDisputeTitle', { subject }), body: t('projectView.contractTodo.externalDisputeBody') };
    }
    if (milestone.deliveryStatus === 'under_resolution') {
      return {
        title: t(inConflict ? 'projectView.contractTodo.conflictTitle' : 'projectView.contractTodo.remedyTitle', { subject }),
        body: t(isPerson ? 'projectView.contractTodo.remedyBodyPerson' : 'objectionCase.next.companyUnderResolution.text')
      };
    }
    if (inConflict) {
      return { title: t('projectView.contractTodo.conflictTitle', { subject }), body: t('projectView.contractTodo.conflictBody') };
    }
    return {
      title: t('projectView.overview.notice.objectionTitle', { stage: subject }),
      // Without turn information the viewer is asked to reply (start page).
      body: objectionTurn
        ? t(isPerson ? 'projectView.overview.notice.personTurn' : 'projectView.overview.notice.companyTurn')
        : t('projectView.contractTodo.objectionBody')
    };
  };
  return { t, subjectLabel, attestLabel, dialogueRowText };
};

interface ContractActionsProps {
  contract: ActionContract;
  milestones: Milestone[];
  projectFunded: boolean;
  side: MilestoneActionSide;
  handlers: MilestoneActionHandlers;
  /** What each stage pays out, in öre, when the server knows it (after fees). */
  payoutOreByMilestone?: Record<string, number | null | undefined>;
  /** Whose turn it is in a stage's objection dialogue; without it the viewer is always asked to reply. */
  objectionTurn?: (milestone: Milestone) => MilestoneActionSide;
}

/** Whether MilestoneContractActions has anything to show for the viewer. */
export const hasContractMilestoneActions = (
  contract: ActionContract,
  milestones: Milestone[],
  projectFunded: boolean,
  side: MilestoneActionSide,
  objectionTurn?: (milestone: Milestone) => MilestoneActionSide
) => {
  const { attestableMilestones, disputeDialogueMilestone, milestoneActionTarget } =
    contractMilestoneActionState(contract, milestones, projectFunded, side);
  if (objectionAwaitsOtherParty(disputeDialogueMilestone, side, objectionTurn)) return false;
  return !!milestoneActionTarget || attestableMilestones.length > 0;
};

/** The contract's action row under its timeline: attest, respond in a conflict, or handle a remedy. */
export function MilestoneContractActions({ contract, milestones, projectFunded, side, handlers, payoutOreByMilestone, objectionTurn }: ContractActionsProps) {
  const { t, subjectLabel, attestLabel, dialogueRowText } = useActionLabels();
  const { i18n } = useTranslation();
  // The company sees what the attestation releases to it: the server's figure
  // (after fees) when known. The client sees the stage's amount after ROT —
  // the funds they deposited for it — never the contractor's net or fees
  // (ClickUp 869fj6jt9).
  const payoutLabel = (milestone: Milestone) => {
    const ore = payoutOreByMilestone?.[milestone.id];
    const amount = side === 'person'
      ? milestone.afterRotAmount ?? milestone.amount
      : typeof ore === 'number' ? ore / 100 : milestone.payoutAmountAfterCredits ?? milestone.afterRotAmount ?? milestone.amount;
    if (!amount || amount <= 0) return null;
    return new Intl.NumberFormat(i18n.language === 'en' ? 'en-GB' : 'sv-SE', { style: 'currency', currency: 'SEK' }).format(amount);
  };
  const isPerson = side === 'person';
  const { attestableMilestones, disputeDialogueMilestone, milestoneActionTarget } =
    contractMilestoneActionState(contract, milestones, projectFunded, side);
  if (!milestoneActionTarget && attestableMilestones.length === 0) return null;
  if (objectionAwaitsOtherParty(disputeDialogueMilestone, side, objectionTurn)) return null;
  const { onOpen, onAttestRemedied, preparingMilestoneId } = handlers;
  const dialogueText = disputeDialogueMilestone
    ? dialogueRowText(disputeDialogueMilestone, side, objectionTurn)
    : { title: '', body: '' };

  return (
    <div className="d-flex flex-wrap gap-2 mb-2 px-1">
      {disputeDialogueMilestone ? (
        <div
          className="contract-action-item d-flex gap-2 align-items-center"
          data-testid={`contract-dialogue-item-${disputeDialogueMilestone.id}`}
        >
          <span className="contract-action-text">
            <span className="contract-action-description small fw-semibold">{dialogueText.title}</span>
            <span className="small text-muted">{dialogueText.body}</span>
          </span>
          {disputeDialogueMilestone.deliveryStatus === 'under_resolution' ? (
            <MilestoneRemediationActions
              deliveryStatus={disputeDialogueMilestone.deliveryStatus}
              isPerson={isPerson}
              attestLabel={attestLabel(disputeDialogueMilestone)}
              preparingLabel={t('projectView.dispute.preparingAttestation')}
              conflictLabel={t('projectView.dispute.openConflictManagement')}
              isPreparing={!isPerson && preparingMilestoneId === disputeDialogueMilestone.id}
              compact
              dataTestId={`milestone-remediation-actions-contract-${disputeDialogueMilestone.id}`}
              attestDataTestId={`contract-remedy-attest-${disputeDialogueMilestone.id}`}
              onAttest={() => {
                if (isPerson || !onAttestRemedied) onOpen(disputeDialogueMilestone);
                else onAttestRemedied(disputeDialogueMilestone);
              }}
              onOpenConflict={() => onOpen(disputeDialogueMilestone, 'resolution')}
            />
          ) : (
            <MilestoneConflictActionButton
              deliveryStatus={disputeDialogueMilestone.deliveryStatus}
              compact
              neutral={disputeDialogueMilestone.deliveryStatus === 'external_dispute'}
              dataTestId={`contract-conflict-cta-${disputeDialogueMilestone.id}`}
              onClick={() => onOpen(disputeDialogueMilestone)}
            >
              {disputeDialogueMilestone.deliveryStatus === 'external_dispute'
                ? t('projectView.dispute.showDisputeExport')
                : isPerson
                  ? t('projectView.dispute.manageSubject', { subject: subjectLabel(disputeDialogueMilestone) })
                  : t('projectView.dispute.respondSubject', { subject: subjectLabel(disputeDialogueMilestone) })}
            </MilestoneConflictActionButton>
          )}
        </div>
      ) : <div className="attest-box" data-testid="attest-box">
        {/* Why attesting matters, once above the stages: the payment is only
            released by the viewer's attestation. Shown only while a stage waits. */}
        <div className="attest-payout-notice" data-testid="attest-payout-notice">
          <span className="attest-payout-notice-icon" aria-hidden="true">{t('projectView.attestPayout.currencyMark')}</span>
          <span>
            <strong>{t(isPerson ? 'projectView.attestPayout.personTitle' : 'projectView.attestPayout.companyTitle')}</strong>{' '}
            {t(isPerson ? 'projectView.attestPayout.personBody' : 'projectView.attestPayout.companyBody', { count: attestableMilestones.length })}
          </span>
        </div>
        {attestableMilestones.map(candidate => {
        const payout = payoutLabel(candidate);
        return (
        <div key={candidate.id} className="contract-action-item d-flex gap-2 align-items-center">
          <span className="contract-action-text">
            <span
              id={`contract-stage-description-${candidate.id}`}
              className="contract-action-description small text-muted text-truncate"
            >
              {candidate.description}
            </span>
            {payout && <span className="contract-action-payout" data-testid={`contract-attest-payout-${candidate.id}`}>
              {t(isPerson ? 'projectView.attestPayout.releasesPerson' : 'projectView.attestPayout.releasesCompany', { amount: payout })}
            </span>}
          </span>
          <button
            className="btn btn-sm btn-primary flex-shrink-0"
            data-testid={`contract-attest-${candidate.id}`}
            onClick={() => onOpen(candidate)}
          >
            <i className="feather-flag me-1"></i>
            {milestoneAttestationActionLabel(t('personHome.projectTimeline.attest', 'Attestera etappmål'), candidate)}
          </button>
          {isPerson && (
            <button
              className="btn btn-sm btn-outline-secondary flex-shrink-0"
              onClick={() => onOpen(candidate, 'resolution')}
              aria-describedby={`contract-stage-description-${candidate.id}`}
            >
              <i className="feather-help-circle me-1" aria-hidden="true"></i>
              {t('milestoneStarts.reportProblem')}
            </button>
          )}
        </div>
        );
        })}
      </div>}
    </div>
  );
}

interface NodeActionsProps {
  milestone: Milestone;
  contract: ActionContract;
  projectFunded: boolean;
  side: MilestoneActionSide;
  handlers: MilestoneActionHandlers;
  closeDetails: () => void;
}

/** The actions in a stage's details on the flow timeline. */
export function MilestoneNodeActions({ milestone: candidate, contract, projectFunded, side, handlers, closeDetails }: NodeActionsProps) {
  const { t, subjectLabel, attestLabel } = useActionLabels();
  const isPerson = side === 'person';
  const { onOpen, onAttestRemedied, preparingMilestoneId } = handlers;
  const contractFullySigned = contract.companySigned && contract.personSigned;
  const candidateCanAttest = contract.draftStatus === 'signed_by_both' &&
    canAttestMilestone(candidate, side);
  const candidateCanReopenConflict =
    candidate.deliveryStatus === 'ready_for_reattestation' &&
    (!isPerson || Boolean(candidate.conflictStartedAt));
  const candidateInDialogue = ['disputed', 'conflict', 'external_dispute'].includes(candidate.deliveryStatus ?? '');
  const candidateAttestLabel = attestLabel(candidate);
  const openCandidate = (mode: MilestoneActionMode = 'attestation') => {
    closeDetails();
    onOpen(candidate, mode);
  };
  // A neutral way to raise a problem with an ongoing stage: conflict mode is
  // only started inside the modal, never straight from the timeline (ClickUp 869fj6n8x).
  const canReportProblem = contractFullySigned &&
    ['in_progress', 'ready_for_attestation', 'seller_attested'].includes(candidate.deliveryStatus ?? '') &&
    !(candidate.companyAttestation === 'approved' && candidate.personAttestation === 'approved');
  const reportProblemAction = (
    <button
      type="button"
      className="btn btn-outline-secondary"
      data-testid={`milestone-flow-report-problem-${candidate.id}`}
      onClick={() => openCandidate('resolution')}
    >
      <i className="feather-help-circle me-1" aria-hidden="true"></i>
      {t('milestoneStarts.stageProblem')}
    </button>
  );

  if (!isPerson && (candidate.payoutAmountAfterCredits ?? candidate.afterRotAmount) > 0 && handlers.onCheckPayout && projectFunded && contractFullySigned &&
    candidate.companyAttestation === 'approved' && candidate.personAttestation === 'approved' &&
    ['jointly_attested', 'payout_processing', 'payout_failed'].includes(candidate.deliveryStatus ?? '')) {
    return <button type="button" className="btn btn-outline-primary"
      disabled={Boolean(handlers.checkingPayoutId)}
      data-testid={`milestone-flow-check-payout-${candidate.id}`}
      onClick={() => { closeDetails(); handlers.onCheckPayout?.(candidate); }}>
      {t('milestoneAttestation.checkPayout')}
    </button>;
  }

  if (candidate.deliveryStatus === 'under_resolution') {
    return (
      <MilestoneRemediationActions
        deliveryStatus={candidate.deliveryStatus}
        isPerson={isPerson}
        attestLabel={candidateAttestLabel}
        preparingLabel={t('projectView.dispute.preparingAttestation')}
        conflictLabel={t('projectView.dispute.openConflictManagement')}
        isPreparing={!isPerson && preparingMilestoneId === candidate.id}
        dataTestId={`milestone-flow-remediation-${candidate.id}`}
        attestDataTestId={`milestone-flow-remedy-attest-${candidate.id}`}
        onAttest={() => {
          closeDetails();
          if (isPerson || !onAttestRemedied) onOpen(candidate);
          else onAttestRemedied(candidate);
        }}
        onOpenConflict={() => openCandidate('resolution')}
      />
    );
  }

  if (candidateInDialogue) {
    return (
      <MilestoneConflictActionButton
        deliveryStatus={candidate.deliveryStatus}
        dataTestId={`milestone-flow-conflict-${candidate.id}`}
        onClick={() => openCandidate()}
      >
        {candidate.deliveryStatus === 'external_dispute'
          ? t('projectView.dispute.showDisputeExport')
          : isPerson
            ? t('projectView.dispute.manageSubject', { subject: subjectLabel(candidate) })
            : t('projectView.dispute.respondSubject', { subject: subjectLabel(candidate) })}
      </MilestoneConflictActionButton>
    );
  }

  if (!candidateCanAttest) {
    if (canReportProblem) return reportProblemAction;
    // Nothing to do: the stage's action row says so itself.
    return null;
  }

  return (
    <>
      <button
        type="button"
        className="btn btn-primary"
        data-testid={`milestone-flow-attest-${candidate.id}`}
        onClick={() => openCandidate()}
      >
        <i className="feather-flag me-1" aria-hidden="true"></i>
        {candidateAttestLabel}
      </button>
      {candidateCanReopenConflict ? (
        <MilestoneConflictActionButton
          deliveryStatus={candidate.deliveryStatus}
          dataTestId={`milestone-flow-reopen-conflict-${candidate.id}`}
          onClick={() => openCandidate('resolution')}
        >
          {t('projectView.dispute.openConflictManagement')}
        </MilestoneConflictActionButton>
      ) : (canReportProblem || isPerson) && reportProblemAction}
    </>
  );
}
